import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Ruler, Weight, Zap, ShoppingBag } from "lucide-react";
import { fetchOne } from "../api";
import { Loader } from "../components/Shared";

export default function WrestlerDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);

  useEffect(() => { fetchOne("wrestlers", id).then(setW).catch(() => setW(false)); }, [id]);

  if (w === null) return <Loader />;
  if (w === false) return <div className="pt-40 px-6 text-center min-h-screen"><p className="text-zinc-400">Wrestler not found.</p></div>;

  const stats = [
    { icon: MapPin, label: "From", value: w.hometown },
    { icon: Ruler, label: "Height", value: w.height },
    { icon: Weight, label: "Weight", value: w.weight },
    { icon: Zap, label: "Finisher", value: w.finisher },
  ].filter((s) => s.value);
  const hasRecord = (w.wins || 0) + (w.losses || 0) > 0;

  return (
    <div data-testid="wrestler-detail-page" className="pt-24">
      <div className="px-6 md:px-12 lg:px-20 py-6">
        <Link to="/roster" data-testid="back-to-roster" className="inline-flex items-center gap-2 text-zinc-400 hover:text-brand font-mono uppercase text-sm tracking-wider">
          <ArrowLeft size={16} /> Back to Roster
        </Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        <div className="relative min-h-[60vh] lg:min-h-[80vh]">
          <img src={w.detail_image || w.image} alt={w.name} className="w-full h-full object-cover absolute inset-0" style={{ objectPosition: w.image_pos || "top" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent lg:bg-gradient-to-r" />
        </div>
        <div className="px-6 md:px-12 lg:px-16 py-12 flex flex-col justify-center spotlight">
          {w.nickname && <p className="text-brand font-mono uppercase tracking-widest">{w.nickname}</p>}
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.85] tracking-tighter mt-1">{w.name}</h1>

          {w.affiliation && (
            <div className="mt-6 flex items-center gap-4" data-testid="wrestler-affiliation">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">In affiliation with</span>
              <img src={w.affiliation} alt="Affiliation" className="h-12 w-auto border border-zinc-800" />
            </div>
          )}

          {hasRecord && (
            <div className="flex gap-8 mt-8">
              <div><div className="font-display text-5xl text-brand leading-none">{w.wins}</div><div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-1">Wins</div></div>
              <div><div className="font-display text-5xl text-zinc-600 leading-none">{w.losses}</div><div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-1">Losses</div></div>
            </div>
          )}

          {w.bio && <p className="text-zinc-300 leading-relaxed mt-8 max-w-lg whitespace-pre-line">{w.bio}</p>}

          {stats.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mt-8 max-w-lg">
              {stats.map((s) => (
                <div key={s.label} className="border border-zinc-800 p-4">
                  <div className="flex items-center gap-2 text-brand"><s.icon size={15} /><span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</span></div>
                  <div className="text-white mt-1 font-medium">{s.value}</div>
                </div>
              ))}
            </div>
          )}
          {w.merch_url && (() => {
            const isMerch = /prowrestlingtees|shop|store|tees|merch/i.test(w.merch_url);
            const first = w.name.split(" ")[0];
            return (
              <a href={w.merch_url} target="_blank" rel="noreferrer" data-testid="wrestler-merch-btn"
                className="mt-8 inline-flex items-center gap-3 bg-brand hover:bg-brand-hi text-black font-display text-xl uppercase tracking-wide px-7 py-4 transition-colors w-fit">
                <ShoppingBag size={20} /> {isMerch ? `Shop ${first}'s Merch` : `Visit ${first}'s Site`}
              </a>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
